/**
 * Void Renderer — magenta kill floor with gradient fill, liquid sine-wave edge,
 * animated bubbles, glitch bars + tear lines, ambient particles, and
 * 4-phase rise choreography (anticipation → flash → rise → settle).
 *
 * Blade effects: razor-edge layering, slash marks, sweep trail, proximity aggression.
 */

import { Container, Graphics } from 'pixi.js';
import { GlowFilter } from 'pixi-filters/glow';
import { Camera } from 'bonkjs';
import { VOID, Z } from '../../constants';
import type { ParticleSystem } from '../particles';
import type { ScreenEffects } from '../effects';
import type { ScreenShake } from '../screen-shake';
import type { VoidState, PlayerPos } from './types';
import { setVoidTheme, getVoidTheme } from './types';
import { drawLiquidEdge } from './edge';
import { drawGradient } from './gradient';
import { updateSweepTrail } from './sweep';
import { updateRiseChoreography } from './rise-choreography';
import { updateSlashMarks, spawnSlashMarksAt } from './slash-marks';
import { updateBubbles } from './bubbles';
import { updateGlitch, randomizeGlitchBars } from './glitch';
import { updateAmbientMotes } from './ambient';

export class VoidRenderer {
  private container: Container;
  private sweepGfx: Graphics;
  private gradientGfx: Graphics;
  private bubbleGfx: Graphics;
  private edgeGfx: Graphics;
  private slashGfx: Graphics;
  private glitchGfx: Graphics;
  private ambientGfx: Graphics;

  private state: VoidState;

  constructor(worldContainer: Container) {
    this.container = new Container();
    this.container.zIndex = Z.VOID;
    worldContainer.addChild(this.container);

    // Sweep trail — behind gradient (first child)
    this.sweepGfx = new Graphics();
    this.container.addChild(this.sweepGfx);

    // Gradient fill — series of horizontal strips
    this.gradientGfx = new Graphics();
    this.container.addChild(this.gradientGfx);

    // Bubble graphics — between gradient and edge
    this.bubbleGfx = new Graphics();
    this.container.addChild(this.bubbleGfx);

    // Edge glow line — magenta with glow filter
    this.edgeGfx = new Graphics();
    this.edgeGfx.filters = [new GlowFilter({
      color: VOID.EDGE_COLOR,
      distance: VOID.EDGE_GLOW_DISTANCE,
      outerStrength: VOID.EDGE_GLOW_STRENGTH,
      innerStrength: 0,
    })];
    this.container.addChild(this.edgeGfx);

    // Slash marks — between edge and glitch
    this.slashGfx = new Graphics();
    this.container.addChild(this.slashGfx);

    // Animated glitch bars + tear lines
    this.glitchGfx = new Graphics();
    this.container.addChild(this.glitchGfx);

    // Ambient particles (self-contained, drawn on Graphics)
    this.ambientGfx = new Graphics();
    this.container.addChild(this.ambientGfx);

    // Initialize shared state
    this.state = {
      edgeDistortionX: 0,
      edgeDistortionStrength: 0,
      edgeWaveAmp: 1,
      edgeTime: 0,
      cachedEdgeYs: [],
      cachedEdgeSegWidth: 0,
      riseIntensity: 0,
      risePhaseTimer: 0,
      risePhaseActive: false,
      moodIntensity: 0,
      phase2Fired: false,
      phase3Fired: false,
      previousVoidY: -1,
      sweepTrailAlpha: 0,
      sweepTrailStartY: -1,
      lastDrawnVoidY: -1,
      gradientFrameSkip: 0,
      glitchBars: [],
      glitchTimer: 0,
      ambientMotes: [],
      bubbles: [],
      slashMarks: [],
      playerPositions: [],
      particlesRef: null,
      effectsRef: null,
      cameraRef: null,
      screenShakeRef: null,
    };

    // Initialize glitch bars
    randomizeGlitchBars(VOID.GLITCH_COUNT, this.state);
  }

  /** Update the active stage theme — rebuilds glow filter with new edge color. */
  setTheme(stageId: string): void {
    setVoidTheme(stageId);
    const theme = getVoidTheme();
    this.edgeGfx.filters = [new GlowFilter({
      color: theme.voidEdgeColor,
      distance: VOID.EDGE_GLOW_DISTANCE,
      outerStrength: VOID.EDGE_GLOW_STRENGTH,
      innerStrength: 0,
    })];
  }

  /** Set player positions for proximity aggression effects. */
  setPlayerPositions(positions: PlayerPos[]): void {
    this.state.playerPositions = positions;
  }

  /** Set mood intensity (0-100 game value mapped to 0-1 visual). */
  setMood(mood: number): void {
    this.state.moodIntensity = Math.min(1, Math.max(0, mood / 100));
  }

  update(dt: number, voidY: number): void {
    this.state.edgeTime += dt;

    // Rise choreography
    updateRiseChoreography(dt, voidY, this.state);

    // Mood-driven baseline: when not actively rising, blend mood into riseIntensity
    // This drives all existing subsystems (bubbles, glitch, slash marks, ambient, edge glow)
    if (!this.state.risePhaseActive) {
      const moodFloor = this.state.moodIntensity * 0.35;
      if (this.state.riseIntensity < moodFloor) {
        this.state.riseIntensity = moodFloor;
      }
    }

    // Edge wave amplitude decay toward target (for cases outside choreography)
    if (!this.state.risePhaseActive && this.state.edgeWaveAmp > 1.01) {
      this.state.edgeWaveAmp = 1 + (this.state.edgeWaveAmp - 1) * Math.pow(VOID.EDGE_AMP_DECAY, dt * 60);
      if (this.state.edgeWaveAmp < 1.01) this.state.edgeWaveAmp = 1;
    }

    // Sweep trail (must update before gradient so previousVoidY is tracked)
    updateSweepTrail(this.sweepGfx, dt, voidY, this.state);

    // Redraw gradient when voidY changes; throttle during rise
    if (voidY !== this.state.lastDrawnVoidY) {
      // voidY changed — always redraw immediately
      drawGradient(this.gradientGfx, voidY, this.state);
      this.state.lastDrawnVoidY = voidY;
      this.state.gradientFrameSkip = 0;
    } else if (this.state.riseIntensity > 0) {
      // Rise active — throttle to every 3rd frame
      this.state.gradientFrameSkip++;
      if (this.state.gradientFrameSkip >= 3) {
        drawGradient(this.gradientGfx, voidY, this.state);
        this.state.gradientFrameSkip = 0;
      }
    } else if (this.state.gradientFrameSkip !== 0) {
      // Rise just ended — one final clean redraw at riseIntensity=0
      drawGradient(this.gradientGfx, voidY, this.state);
      this.state.gradientFrameSkip = 0;
    }

    // Edge distortion decay (kill bump)
    if (this.state.edgeDistortionStrength > 0) {
      this.state.edgeDistortionStrength *= Math.pow(0.92, dt * 60);
      if (this.state.edgeDistortionStrength < 0.5) this.state.edgeDistortionStrength = 0;
    }

    // Always redraw liquid edge (animates continuously)
    drawLiquidEdge(this.edgeGfx, voidY, this.state);

    // Slash marks
    updateSlashMarks(this.slashGfx, dt, voidY, this.state);

    // Bubbles
    updateBubbles(this.bubbleGfx, dt, voidY, this.state);

    // Glitch bars + tear lines
    updateGlitch(this.glitchGfx, dt, voidY, this.state);

    // Ambient particles
    updateAmbientMotes(this.ambientGfx, dt, voidY, this.state);
  }

  /** Begin 4-phase rise choreography. Stores refs for delayed effects. */
  triggerRiseEffects(
    _voidY: number,
    particles: ParticleSystem,
    effects: ScreenEffects,
    camera: Camera,
    screenShake: ScreenShake,
  ): void {
    // Store references for delayed effects in choreography phases
    this.state.particlesRef = particles;
    this.state.effectsRef = effects;
    this.state.cameraRef = camera;
    this.state.screenShakeRef = screenShake;

    // Activate choreography
    this.state.risePhaseActive = true;
    this.state.risePhaseTimer = 0;
    this.state.phase2Fired = false;
    this.state.phase3Fired = false;
    this.state.riseIntensity = 0;
  }

  /** Visual pulse on void edge at kill location. */
  triggerEdgeDistortion(x: number, _voidY: number): void {
    this.state.edgeDistortionX = x;
    this.state.edgeDistortionStrength = 30; // px upward bulge, decays
  }

  /** Foreshadow effect — localized warning before void retaliation orb launch. */
  triggerForeshadow(x: number, voidY: number): void {
    // Localized edge distortion (15px bump)
    this.state.edgeDistortionX = x;
    this.state.edgeDistortionStrength = 15;

    // 3-5 slash marks near launch X
    const count = 3 + Math.floor(Math.random() * 3);
    spawnSlashMarksAt(x, voidY, count, 80, 1, 1, 0.6, 0.9, this.state);
  }

  /** Localized slash mark burst + stronger distortion at void kill location. */
  triggerConsumeBurst(x: number, voidY: number): void {
    // Stronger edge distortion
    this.state.edgeDistortionX = x;
    this.state.edgeDistortionStrength = 50;

    // 10 slash marks near kill X (spread ±80px), 1.5x longer life + larger arc + higher alpha
    spawnSlashMarksAt(x, voidY, 10, 160, 1.5, 1.5, 0.7, 1.0, this.state);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
